import { createTheme, ThemeOptions } from "@mui/material/styles";
// Types the date pickers' theme keys (MuiPickersInputBase, ...).
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { brighten } from "@/lib/ortex-charts/color";

// Text colour on a teal (ortexColor) fill in both themes; mirrors --on-accent in redesign.scss.
export const ON_ORTEX_COLOR = "#06100f";

// Every progress indicator is an ARIA progressbar and needs a name; a spinner that means
// something more specific passes its own aria-label.
const progressNames = {
    MuiCircularProgress: { defaultProps: { "aria-label": "Loading" } },
    MuiLinearProgress: { defaultProps: { "aria-label": "Loading" } },
};

export const getThemes = ({ ortexColor, positive, negative, neutral, textColor, greyMedium, greyLighter, backgroundColor, darkerBackgroundColor, darkSurfaceDefaultColor, newbackgroundColor, whiteColor }: any) => {
    const defaultTheme = createTheme({
        typography: {
            fontFamily: '"Inter", "Roboto", "Segoe UI", system-ui, sans-serif',
            moduleContentTitle: {
                fontWeight: 700,
                fontSize: 12,
                "@media screen and (min-width: 768px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 16,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 22,
                },
            },
            body1: {
                fontSize: 12,
                "@media screen and (min-width: 768px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1440px)": {
                    fontSize: 14,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 16,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 22,
                },
            },
            body2: {
                fontSize: 10,
                "@media screen and (min-width: 768px)": {
                    fontSize: 10,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 10,
                },
                "@media screen and (min-width: 1440px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 14,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 20,
                },
            },
            body3: {
                fontSize: 10,
                "@media screen and (min-width: 768px)": {
                    fontSize: 10,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 10,
                },
                "@media screen and (min-width: 1440px)": {
                    fontSize: 11,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 18,
                },
            },
            h1: {
                fontWeight: 500,
                fontSize: 18,
                "@media screen and (min-width: 768px)": {
                    fontSize: 18,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 18,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 20,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 26,
                },
            },
            h2: {
                fontWeight: 500,
                fontSize: 16,
                "@media screen and (min-width: 768px)": {
                    fontSize: 16,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 16,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 18,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 26,
                },
            },
            h3: {
                fontSize: 12,
                "@media screen and (min-width: 768px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1280px)": {
                    fontSize: 12,
                },
                "@media screen and (min-width: 1920px)": {
                    fontSize: 14,
                },
                "@media screen and (min-width: 3840px)": {
                    fontSize: 20,
                },
            },

            subtitle2: {
                fontSize: 10,
                "@media screen and (min-width: 768px)": { fontSize: 10 },
                "@media screen and (min-width: 1280px)": { fontSize: 10 },
                "@media screen and (min-width: 1920px)": { fontSize: 12 },
                "@media screen and (min-width: 3840px)": { fontSize: 16 },
            },
            overline: {
                fontWeight: 100,
                fontSize: 10,
                "@media screen and (min-width: 768px)": { fontSize: 10 },
                "@media screen and (min-width: 1280px)": { fontSize: 10 },
                "@media screen and (min-width: 1920px)": { fontSize: 12 },
                "@media screen and (min-width: 3840px)": { fontSize: 18 },
            },
            h5userMenu: {
                fontWeight: 100,
                fontSize: 11,
                "@media screen and (min-width: 768px)": { fontSize: 12 },
                "@media screen and (min-width: 1280px)": { fontSize: 12 },
                "@media screen and (min-width: 1920px)": { fontSize: 14 },
                "@media screen and (min-width: 3840px)": { fontSize: 18 },
            },
            captionSessionChip: {
                fontSize: 12,
                "@media screen and (min-width: 768px)": { fontSize: 12 },
                "@media screen and (min-width: 1280px)": { fontSize: 12 },
                "@media screen and (min-width: 1920px)": { fontSize: 12 },
                "@media screen and (min-width: 3840px)": { fontSize: 16 },
            },
            newsCardDate: {
                fontWeight: 600,
                fontSize: 12,
                "@media screen and (min-width: 768px)": { fontSize: 12 },
                "@media screen and (min-width: 1280px)": { fontSize: 12 },
                "@media screen and (min-width: 1920px)": { fontSize: 14 },
                "@media screen and (min-width: 3840px)": { fontSize: 20 },
            },
            minorSubtitle: {
                fontSize: 8,
                "@media screen and (min-width: 768px)": { fontSize: 8 },
                "@media screen and (min-width: 1280px)": { fontSize: 9 },
                "@media screen and (min-width: 1920px)": { fontSize: 10 },
                "@media screen and (min-width: 3840px)": { fontSize: 14 },
            },
            tabs: {
                fontWeight: 700,
                fontSize: 8,
                "@media screen and (min-width: 768px)": { fontSize: 9 },
                "@media screen and (min-width: 1280px)": { fontSize: 9 },
                "@media screen and (min-width: 1920px)": { fontSize: 10 },
                "@media screen and (min-width: 3840px)": { fontSize: 14 },
            },
        },
        components: {
            ...progressNames,
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        h5userMenu: "h5",
                        captionSessionChip: "caption",
                        newsCardDate: "body2",
                        minorSubtitle: "subtitle2",
                    },
                },
            },
        },
    });

    const light = createTheme({
        ...(defaultTheme as ThemeOptions),
        components: {
            ...progressNames,
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: newbackgroundColor,
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: textColor,
                        backgroundColor: backgroundColor,
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    icon: {
                        color: "#000",
                    },
                },
            },
            // Date pickers draw their own field (PickersTextField), which MuiInputBase no longer reaches.
            MuiPickersInputBase: {
                styleOverrides: {
                    root: {
                        color: textColor,
                        backgroundColor: backgroundColor,
                    },
                },
            },
            // MUI 9 shrank the default to 36px; the side menu and lists were laid out on 56px.
            // (MenuItem keeps its own 36px.)
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        minWidth: 56,
                    },
                },
            },
        },
        palette: {
            background: {
                default: newbackgroundColor,
            },
            primary: {
                main: ortexColor,
                // Dark ink on the teal: 6.9:1, where white is 2.8:1 (WCAG AA needs 4.5).
                contrastText: ON_ORTEX_COLOR,
            },
            info: {
                main: "#3b97ef",
            },
            text: {
                primary: textColor,
            },
            success: {
                main: positive,
            },
            error: {
                main: negative,
            },
        },
    });

    const dark = createTheme({
        ...(defaultTheme as ThemeOptions),
        components: {
            ...progressNames,
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: darkSurfaceDefaultColor,
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        color: textColor,
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        color: brighten(whiteColor, -0.1),
                    },
                },
            },
            MuiInput: {
                styleOverrides: {
                    underline: {
                        "&&::before": {
                            borderColor: "rgb(128, 128, 128)",
                        },
                        "&&:hover::before": {
                            borderColor: greyLighter,
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: textColor,
                        backgroundColor: backgroundColor,
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgb(128, 128, 128)",
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    icon: {
                        color: "#e6e6e6",
                    },
                },
            },
            // Date pickers draw their own field (PickersTextField): mirror MuiInput / MuiInputBase above.
            MuiPickersInput: {
                styleOverrides: {
                    root: {
                        "&&::before": {
                            borderColor: "rgb(128, 128, 128)",
                        },
                        "&&:hover::before": {
                            borderColor: greyLighter,
                        },
                    },
                },
            },
            MuiPickersInputBase: {
                styleOverrides: {
                    root: {
                        color: textColor,
                        backgroundColor: backgroundColor,
                    },
                },
            },
            MuiPickersOutlinedInput: {
                styleOverrides: {
                    notchedOutline: {
                        border: "1px solid rgb(128, 128, 128)",
                    },
                },
            },
            // MUI 9 shrank the default to 36px; the side menu and lists were laid out on 56px.
            // (MenuItem keeps its own 36px.)
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        minWidth: 56,
                    },
                },
            },
        },
        palette: {
            background: {
                default: darkerBackgroundColor,
                // Match the surface color forced onto MuiPaper above. Without this,
                // components that read palette.background.paper (e.g. ListSubheader)
                // fall back to MUI's default white and clash with the dark menu.
                paper: darkSurfaceDefaultColor,
            },
            primary: {
                dark: "#0f524a",
                main: ortexColor,
                // Dark ink on the teal: 6.9:1, where white is 2.8:1 (WCAG AA needs 4.5).
                contrastText: ON_ORTEX_COLOR,
                light: "#e1f3f3",
            },
            secondary: {
                dark: "#0f524a",
                main: ortexColor,
                // Dark ink on the teal: 6.9:1, where white is 2.8:1 (WCAG AA needs 4.5).
                contrastText: ON_ORTEX_COLOR,
                light: "#e1f3f3",
            },
            info: {
                main: "#3b97ef",
            },
            text: {
                disabled: "rgb(100, 110, 126)",
                primary: textColor,
                secondary: "rgb(151, 161, 178)",
            },
        },
    });

    return { defaultTheme, light, dark };
};
